import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { libraryStats, listItems } from "@/lib/api";
import { ArrowRight, Layers, ScanLine, FileText } from "lucide-react";

const quickActions = [
  {
    to: "/builder",
    title: "Crear una oferta",
    desc: "Te ayudo a ponerle precio a tu trabajo",
    icon: Layers,
    tag: "tag-pill-mint",
    blob: "blob-mint",
    testid: "qa-builder",
  },
  {
    to: "/analyzer",
    title: "Analizar un cliente",
    desc: "Sabrás si es buena idea aceptarlo",
    icon: ScanLine,
    tag: "tag-pill-amber",
    blob: "blob-lemon",
    testid: "qa-analyzer",
  },
  {
    to: "/proposal",
    title: "Escribir propuesta",
    desc: "Documento premium listo para enviar",
    icon: FileText,
    tag: "tag-pill-rose",
    blob: "blob-rose",
    testid: "qa-proposal",
  },
];

export default function Dashboard() {
  const [stats, setStats] = useState({ offers: 0, clients: 0, proposals: 0, total: 0 });
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [s, r] = await Promise.all([libraryStats(), listItems()]);
        setStats(s);
        setRecent(r.slice(0, 4));
      } catch (e) { console.error(e); }
    })();
  }, []);

  const hour = new Date().getHours();
  const greet = hour < 12 ? "Buenos días" : hour < 19 ? "Buenas tardes" : "Buenas noches";

  return (
    <div className="space-y-14">
      {/* HERO con blobs orgánicos */}
      <section className="relative">
        <div className="blob blob-mint w-[280px] h-[280px] -top-10 -left-10" />
        <div className="blob blob-rose w-[240px] h-[240px] top-20 right-0" style={{ animationDelay: "2s" }} />
        <div className="relative">
          <span className="tag-pill mb-5"><span className="w-1.5 h-1.5 rounded-full bg-[#A8D5BA]" /> Panel</span>
          <h1 className="font-display text-5xl md:text-7xl font-black tracking-[-0.04em] leading-[0.95]">
            {greet},<br />
            <span className="text-foreground/40">¿qué creamos?</span>
          </h1>
          <p className="text-foreground/65 text-base md:text-lg mt-5 max-w-md leading-relaxed">
            Elige una acción rápida o vuelve a tu actividad reciente.
          </p>
          <div className="mt-7 flex gap-3 flex-wrap">
            <Link to="/builder">
              <button className="btn-apple">Nueva oferta <ArrowRight className="w-4 h-4" /></button>
            </Link>
            <Link to="/value-ladder">
              <button className="btn-apple-outline">Ver escalera de valor</button>
            </Link>
          </div>
        </div>
      </section>

      {/* STATS minimalistas */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-px bg-black/[0.06] rounded-3xl overflow-hidden border border-black/[0.06]">
        {[
          { l: "Ofertas", v: stats.offers, k: "Ofertas" },
          { l: "Clientes", v: stats.clients, k: "Clientes" },
          { l: "Propuestas", v: stats.proposals, k: "Propuestas" },
          { l: "Total", v: stats.total, k: "Total" },
        ].map((s) => (
          <div key={s.l} className="bg-white p-6">
            <div className="text-[11px] uppercase tracking-wider font-bold text-foreground/55">{s.l}</div>
            <div className="font-display text-4xl md:text-5xl font-black tracking-[-0.03em] mt-2" data-testid={`stat-${s.k}`}>{s.v}</div>
          </div>
        ))}
      </section>

      {/* QUICK ACTIONS — cards blancas con blob de fondo */}
      <section>
        <div className="flex items-end justify-between mb-6">
          <h2 className="font-display text-2xl md:text-3xl font-black tracking-[-0.02em]">Acciones rápidas</h2>
          <span className="text-sm text-foreground/55">Elige por dónde empezar</span>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          {quickActions.map((a) => (
            <Link key={a.to} to={a.to} data-testid={a.testid} className="block group">
              <div className="app-card p-7 h-full relative min-h-[210px] flex flex-col justify-between">
                <div className={`blob ${a.blob} w-[180px] h-[180px] -top-10 -right-10 opacity-40`} />
                <div className="relative">
                  <div className={`tag-pill ${a.tag} mb-4`}>
                    <a.icon className="w-3 h-3" strokeWidth={2.5} />
                    Empezar
                  </div>
                  <h3 className="font-display text-2xl font-black tracking-[-0.02em]">{a.title}</h3>
                  <p className="text-sm mt-2 text-foreground/65 max-w-[24ch]">{a.desc}</p>
                </div>
                <ArrowRight className="w-5 h-5 text-foreground/40 relative group-hover:text-foreground group-hover:translate-x-1 transition-all" strokeWidth={2} />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* RECIENTE */}
      <section>
        <div className="flex items-end justify-between mb-6">
          <h2 className="font-display text-2xl md:text-3xl font-black tracking-[-0.02em]">Actividad reciente</h2>
          <Link to="/library" className="text-sm font-semibold text-foreground hover:underline" data-testid="see-library">
            Ver todo →
          </Link>
        </div>
        {recent.length === 0 ? (
          <div className="app-card p-12 text-center relative">
            <div className="blob blob-lavender w-[280px] h-[280px] top-0 left-1/2 -translate-x-1/2 opacity-50" />
            <div className="relative">
              <h3 className="font-display text-2xl font-black tracking-[-0.02em]">Aún no has creado nada</h3>
              <p className="text-sm text-foreground/65 mt-2 max-w-sm mx-auto">Empieza creando tu primera oferta. Solo necesitas un minuto.</p>
              <Link to="/builder">
                <button className="mt-6 btn-apple" data-testid="empty-cta">
                  Crear mi primera oferta <ArrowRight className="w-4 h-4" />
                </button>
              </Link>
            </div>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
            {recent.map((it) => {
              const meta = it.kind === "offer"
                ? { label: "Oferta", tag: "tag-pill-mint" }
                : it.kind === "client"
                ? { label: "Cliente", tag: "tag-pill-amber" }
                : { label: "Propuesta", tag: "tag-pill-rose" };
              return (
                <div key={it.id} className="app-card p-5 min-h-[140px] flex flex-col justify-between">
                  <span className={`tag-pill ${meta.tag} self-start`}>{meta.label}</span>
                  <div>
                    <div className="font-display font-black tracking-[-0.01em] text-[15px] line-clamp-2 leading-tight">{it.title}</div>
                    <div className="text-[10px] text-foreground/55 mt-2">{new Date(it.created_at).toLocaleDateString()}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* FILOSOFÍA tipo Apple quote */}
      <section className="app-card-dark p-10 md:p-14 relative">
        <div className="absolute -top-10 -right-10 w-60 h-60 rounded-full bg-[#A8D5BA]/15 blur-2xl" />
        <h3 className="font-display text-2xl md:text-4xl font-black tracking-[-0.02em] max-w-2xl relative leading-tight">
          Cobrar más no es trabajar más. Es asumir{" "}
          <span className="bg-[#EAC119] text-foreground px-2 rounded-xl">más responsabilidad</span>.
        </h3>
        <p className="text-white/60 mt-5 max-w-xl relative">
          Tu cliente no compra tu tiempo. Compra dejar de tener un problema.
        </p>
      </section>
    </div>
  );
}
